'use client';

import {
  FormControl,
  FormLabel,
  FormLabelProps,
  Icon,
  IconButton,
  ChakraProps,
  Tooltip,
  useDisclosure,
  Spinner,
} from '@chakra-ui/react';
import { ReactNode, Ref, useEffect, useState } from 'react';
import {
  Select,
  SelectInstance,
  Props as SelectProps,
  GroupBase,
} from 'chakra-react-select';
import { FieldError } from 'react-hook-form';
import { MdError } from 'react-icons/md';

const isDropdownOption = (data: unknown): data is DropdownOption => {
  if (!data || typeof data !== 'object') return false;

  return 'value' in data && 'label' in data;
};

export type DropdownRef = Ref<
  SelectInstance<unknown, boolean, GroupBase<unknown>>
> | null;

interface Props<T extends DropdownOption = DropdownOption>
  extends ChakraProps,
    Omit<SelectProps, 'onChange'> {
  error?: FieldError;
  label?: ReactNode;
  labelProps?: FormLabelProps;
  options?: T[];
  value?: T['value'];
  onChange?(value: T): void;
  ref?: DropdownRef;
}

export type DropdownProps = Props;

export const Dropdown = (props: DropdownProps) => {
  const {
    error,
    label,
    labelProps,
    onChange,
    value,
    options = [],
    ref,
    ...rest
  } = props;

  const shouldSelectFirstOption = options.length === 1;

  const [isFocused, setIsFocused] = useState(false);
  const [selectedOption, setSelectedOption] = useState<
    DropdownOption | undefined
  >(() => {
    if (!shouldSelectFirstOption) return undefined;

    const [firstOption] = options;

    return firstOption;
  });

  useEffect(() => {
    if (!shouldSelectFirstOption) return;

    const [firstOption] = options;

    setSelectedOption(firstOption);
    onChange?.(firstOption);
  }, []);

  const hasValue = typeof value !== 'undefined';

  useEffect(() => {
    if (!hasValue) return;

    const _selectedOption = options.find(option => option.value === value);

    if (!_selectedOption) return;

    setSelectedOption(_selectedOption);
  }, [value]);

  const { isOpen, onToggle, onOpen, onClose } = useDisclosure();

  const hasError = Boolean(error);
  const hasLabel = Boolean(label);

  const onChangeSelect = (
    newValue: unknown | DropdownOption,
    // actionMeta: ActionMeta<unknown>,
  ) => {
    if (!isDropdownOption(newValue)) return;

    setSelectedOption(newValue);

    onChange?.(newValue);
  };

  const getLabelProps = (): FormLabelProps => {
    if (!hasLabel || (!isFocused && !selectedOption)) return {};

    return {
      transform: 'scale(0.85) translateY(-1.75rem)',
    };
  };

  return (
    <FormControl
      onFocus={() => setIsFocused(true)}
      onBlur={() => setIsFocused(false)}
      pos="relative"
    >
      {hasLabel && (
        <FormLabel
          top={0}
          left={0}
          position="absolute"
          backgroundColor="transparent"
          pointerEvents="none"
          px="0.25rem"
          my="0.5rem"
          w="full"
          transformOrigin="left top"
          {...labelProps}
          {...getLabelProps()}
        >
          {label}
        </FormLabel>
      )}

      <Select
        ref={ref}
        onChange={onChangeSelect}
        defaultValue={selectedOption}
        value={hasValue ? selectedOption : undefined}
        options={options}
        placeholder=""
        isSearchable={false}
        components={{
          LoadingIndicator: () => (
            <Spinner size="xs" speed="0.75s" color="blue.800" thickness="1px" />
          ),
        }}
        {...rest}
      />

      {hasError && (
        <Tooltip
          hasArrow
          isOpen={isOpen}
          onOpen={onOpen}
          onClose={onClose}
          label={error?.message}
          bg="red.500"
          color="white"
        >
          <IconButton
            aria-label="toggle tooltip error"
            onClick={onToggle}
            variant="ghost"
            pos="absolute"
            right="1rem"
            top={0}
          >
            <Icon as={MdError} color="red.500" fontSize="1.25rem" />
          </IconButton>
        </Tooltip>
      )}
    </FormControl>
  );
};
